import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { Button } from '../../components/Button';
import { useNavigate } from 'react-router-dom';
import { useEditor } from '../../providers/EditorContext';

import styles from './styles.module.scss';
import { TextInput } from '../../components/TextInput';
import { SelectInput } from '../../components/SelectInput';
import { range } from '../../utils/range';
import { FormDataValues } from '../../types';
import { api } from '../../api/Api';

import CloseIcon from '../../assets/images/close_icon.png';
import { Title } from '../../components/Title';
import { Subtitle } from '../../components/Subtitle';


export const Checkout: FC = () => {
    const [formData, setFormData] = useState<FormDataValues>({
        name: '',
        phone: '',
        count: 1,
        magnetCount: 0,
    });
    const [pending, setPending] = useState<boolean>(false);
    const [error, setError] = useState<any>(undefined);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const translate = useTranslation();
    const navigate = useNavigate();
    const { resultUrl, clearResultFile } = useEditor();

    const previewFrameRef = useRef<HTMLDivElement>(null);

    const isFormComplete = useMemo(() => {
        return formData.name !== '' && formData.phone !== '' && formData.count + formData.magnetCount > 0;
    }, [formData]);

    const goBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const submitOrder = useCallback(() => {
        if (!clearResultFile) return;

        setPending(true);
        setError(undefined);

        api.submitOrder({
            ...formData,
            file: clearResultFile,
        })
            .then(() => {
                navigate('/finished');
            })
            .catch((e) => {
                setError(e);
            })
            .finally(() => {
                setPending(false);
            });
    }, [formData, clearResultFile, navigate]);

    useEffect(() => {
        if (!resultUrl) {
            navigate('/editor', { replace: true });
        }
    }, [navigate, resultUrl]);

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className="header">
                <Title text={translate('checkoutTitle')} />
                <Subtitle text={translate('checkoutSubtitle')} />
            </div>

            <div className={styles.content}>
                <div className={styles.preview}>
                    {resultUrl ? (
                        <div className={styles.previewFrame} ref={previewFrameRef}>
                            <img
                                className={styles.previewImage}
                                src={resultUrl}
                                alt="Preview"
                                draggable={false}
                                onDragStart={(e) => e.preventDefault()}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        </div>
                    ) : (
                        <p>No preview generated yet</p>
                    )}
                </div>

                <div className={styles.formWrapper}>
                    <p className="disclaimer">{translate('watermarkDisclaimer')}</p>

                    <form className={styles.form}>
                        <TextInput
                            label={translate('checkoutName')}
                            placeHolder={translate('checkoutNamePlaceholder')}
                            onChange={(value) => setFormData({ ...formData, name: value })}
                        />

                        <TextInput
                            label={translate('checkoutPhone')}
                            placeHolder={translate('checkoutPhonePlaceholder')}
                            onChange={(value) => setFormData({ ...formData, phone: value })}
                        />

                        <SelectInput
                            label={translate('count')}
                            placeholder={translate('selectCount')}
                            onChange={(value) => setFormData({ ...formData, count: parseInt(value, 10) })}
                            options={range(100).map((item) => ({
                                value: `${item}`,
                                label: `${item}`,
                            }))}
                            value={`${formData.count}`}
                        />

                        <SelectInput
                            label={translate('magnet')}
                            placeholder={translate('selectMagnetCount')}
                            onChange={(value) => setFormData({ ...formData, magnetCount: parseInt(value, 10) })}
                            options={range(100).map((item) => ({
                                value: `${item}`,
                                label: `${item}`,
                            }))}
                            value={`${formData.magnetCount}`}
                        />
                    </form>
                </div>

                {error && (
                    <div className={styles.error}>
                        <p>{translate('checkoutError')}</p>
                        <img
                            src={CloseIcon}
                            alt="Close"
                            className={styles.closeIcon}
                            onClick={() => setError(undefined)}
                        />
                    </div>
                )}
            </div>

            <div className="footer">
                <div className="footer-content">
                    <Button onClick={goBack} text={translate('goBack')} />
                    <div className="btn-wrapper">
                        <Button
                            disabled={!isFormComplete}
                            onClick={submitOrder}
                            text={translate('placeOrder')}
                            loading={pending}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
